import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, Clock, Loader2, MapPin, Users } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";


import { AddressAutocomplete } from "@/components/booking/AddressAutocomplete";
import { LeadTimeDialog } from "@/components/booking/LeadTimeDialog";
import { TimeSelect } from "@/components/booking/TimeSelect";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listCities } from "@/lib/catalog.functions";
import { earliestPickup, leadTimeOk } from "@/lib/lead-time";
import { closedDateMessage, isClosedDate } from "@/lib/closed-dates";
import { suggestPlaces } from "@/lib/maps.functions";
import { placesQuoteOptions } from "@/lib/quote-query";
import { euro } from "@/lib/pricing";
import { trackPriceCalculated } from "@/lib/tracking";


export type SearchValues = {
  van: string;
  naar: string;
  vanId: string;
  naarId: string;
  datum: string;
  tijd: string;
  personen: number;
};

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  // Lokale kalenderdatum, niet via UTC (toISOString kan een dag verschuiven).
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Standaardmoment dat altijd binnen de reserveringstermijn van 3 uur valt. */
const defaultMoment = () => {
  const first = earliestPickup();
  const date = first.date > tomorrow() ? first.date : tomorrow();
  return { date, time: leadTimeOk(date, "09:00") ? "09:00" : first.time };
};

export function useCities() {
  return useQuery({ queryKey: ["cities"], queryFn: () => listCities(), staleTime: 5 * 60 * 1000 });
}

/** Labels per taal. De formulierlogica en de boekingsflow blijven identiek. */
const COPY = {
  nl: {
    from: "Van",
    to: "Naar",
    date: "Datum",
    time: "Tijd",
    passengers: "Personen",
    passengersAria: "Aantal personen",
    placeholder: "Adres, plaats of luchthaven",
    calculating: "Prijs berekenen\u2026",
    onRequest: "Op aanvraag",
    empty: "Vul uw ophaaladres en bestemming in en kies ze uit de suggesties.",
    emptyFrom: "Vul uw ophaaladres in en kies het uit de suggesties.",
    emptyTo: "Vul uw bestemming in en kies deze uit de suggesties.",
    pickFrom: "Kies uw ophaaladres uit de suggesties.",
    pickTo: "Kies uw bestemming uit de suggesties.",
    same: "Ophaaladres en bestemming mogen niet hetzelfde zijn.",
    timePlaceholder: "Kies tijd",
    timeout:
      "De prijsberekening duurde te lang. Controleer uw internetverbinding en probeer het opnieuw.",
    continueBooking: "Verder boeken",
  },
  en: {
    from: "From",
    to: "To",
    date: "Date",
    time: "Time",
    passengers: "Passengers",
    passengersAria: "Number of passengers",
    placeholder: "Address, town or airport",
    calculating: "Calculating price\u2026",
    onRequest: "On request",
    empty: "Please enter your pick-up address and destination and pick them from the suggestions.",
    emptyFrom: "Please enter your pick-up address and pick it from the suggestions.",
    emptyTo: "Please enter your destination and pick it from the suggestions.",
    pickFrom: "Please select your pick-up address from the suggestions.",
    pickTo: "Please select your destination from the suggestions.",
    same: "Pick-up address and destination cannot be the same.",
    timePlaceholder: "Choose time",
    timeout: "Calculating the price took too long. Please check your connection and try again.",
    continueBooking: "Continue booking",
  },
} as const;


export function SearchForm({
  initial,
  submitLabel,
  locale = "nl",
  prefill,
}: {
  initial?: Partial<SearchValues>;
  submitLabel?: string;
  /** Taal van de labels; de boekingsflow zelf blijft ongewijzigd. */
  locale?: "nl" | "en";
  /**
   * Plaats- of luchthavennamen die alvast in het formulier moeten staan
   * (bijvoorbeeld "Rotterdam" → "Schiphol" op een routepagina). De namen worden
   * via Google Places omgezet naar een echte locatie, zodat de prijs exact
   * dezelfde engine gebruikt als bij het boeken.
   */
  prefill?: { van?: string | null; naar?: string | null };
}) {
  const t = COPY[locale];
  const submit = submitLabel ?? (locale === "en" ? "See price" : "Bekijk prijs");
  const navigate = useNavigate();
  const uid = useId();
  const vanId = `van-${uid}`;
  const naarId = `naar-${uid}`;
  const datumId = `datum-${uid}`;
  const tijdId = `tijd-${uid}`;
  const personenId = `personen-${uid}`;
  const prefillVan = prefill?.van?.trim() ?? "";
  const prefillNaar = prefill?.naar?.trim() ?? "";
  const [values, setValues] = useState<SearchValues>({
    van: initial?.van ?? prefillVan,
    naar: initial?.naar ?? prefillNaar,
    vanId: initial?.vanId ?? "",
    naarId: initial?.naarId ?? "",
    datum: initial?.datum ?? defaultMoment().date,
    tijd: initial?.tijd ?? defaultMoment().time,
    personen: initial?.personen ?? 2,
  });
  const [hint, setHint] = useState<string | null>(null);
  const [leadDialog, setLeadDialog] = useState(false);
  const measuredQuote = useRef("");

  // Vooraf ingevulde plaats/bestemming van de pagina omzetten naar echte
  // locaties, zodat de prijs via dezelfde engine loopt als bij het boeken.
  const { data: prefilled } = useQuery({
    queryKey: ["prefill-places", prefillVan, prefillNaar],
    enabled: Boolean(prefillVan || prefillNaar),
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const pick = async (query: string) => {
        if (query.length < 3) return null;
        const list = await suggestPlaces({ data: { query } });
        const first = list?.[0];
        return first ? { label: first.label, placeId: first.placeId } : null;
      };
      const [van, naar] = await Promise.all([pick(prefillVan), pick(prefillNaar)]);
      return { van, naar };
    },
  });

  useEffect(() => {
    if (!prefilled) return;
    setValues((v) => ({
      ...v,
      ...(prefilled.van && !v.vanId
        ? { van: prefilled.van.label, vanId: prefilled.van.placeId }
        : {}),
      ...(prefilled.naar && !v.naarId
        ? { naar: prefilled.naar.label, naarId: prefilled.naar.placeId }
        : {}),
    }));
  }, [prefilled]);


  const set = <K extends keyof SearchValues>(key: K, value: SearchValues[K]) => {
    const next = { ...values, [key]: value } as SearchValues;
    setValues(next);
    if ((key === "datum" || key === "tijd") && !leadTimeOk(next.datum, next.tijd)) {
      setLeadDialog(true);
    }
    if (key === "datum" && isClosedDate(next.datum)) {
      setHint(closedDateMessage(locale, next.datum));
    }
  };


  const canQuote = values.vanId && values.naarId && values.vanId !== values.naarId;

  // Dezelfde query-opties als het boekingsformulier: de prijs wordt één keer
  // berekend en daarna uit de cache gebruikt.
  const {
    data: quote,
    isFetching: quoting,
    error: quoteError,
  } = useQuery({
    ...placesQuoteOptions({
      fromPlaceId: values.vanId,
      toPlaceId: values.naarId,
      passengers: values.personen,
      timeoutMessage: t.timeout,
    }),
    enabled: Boolean(canQuote),
  });

  const buttonLabel = useMemo(() => {
    if (!canQuote) return submit;
    if (quoteError) return submit;
    if (!quote) return quoting ? t.calculating : submit;
    const customerPrice = (quote as { customerPrice?: number | null }).customerPrice;
    if (customerPrice == null) return t.onRequest;
    // De prijs staat al vast: de knop gaat verder met boeken, niet opnieuw rekenen.
    return `${t.continueBooking} · ${euro(customerPrice)}`;
  }, [canQuote, quote, quoting, quoteError, submit, t]);

  useEffect(() => {
    const customerPrice = (quote as { customerPrice?: number | null } | undefined)?.customerPrice;
    if (customerPrice == null) return;
    const key = `${values.vanId}:${values.naarId}:${values.personen}:${customerPrice}`;
    if (measuredQuote.current === key) return;
    measuredQuote.current = key;
    trackPriceCalculated({ value: customerPrice, passengers: values.personen, locale });
  }, [locale, quote, values.naarId, values.personen, values.vanId]);


  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const fromEmpty = !values.van.trim();
    const toEmpty = !values.naar.trim();
    if (fromEmpty && toEmpty) {
      setHint(t.empty);
      return;
    }
    if (!values.vanId) {
      setHint(fromEmpty ? t.emptyFrom : t.pickFrom);
      return;
    }
    if (!values.naarId) {
      setHint(toEmpty ? t.emptyTo : t.pickTo);
      return;
    }

    if (values.vanId === values.naarId) {
      setHint(t.same);
      return;
    }
    if (quoteError) {
      setHint(quoteError.message);
      return;
    }
    if (!leadTimeOk(values.datum, values.tijd)) {
      setHint(null);
      setLeadDialog(true);
      return;
    }
    if (isClosedDate(values.datum)) {
      setHint(closedDateMessage(locale, values.datum));
      return;
    }
    setHint(null);
    navigate({ to: locale === "en" ? "/en/book" : "/boeken", search: values });
  };


  return (
    <form
      className="grid gap-3 md:grid-cols-[1fr_1fr_auto]"
      onSubmit={handleSubmit}
    >
      <div className="grid gap-3 sm:grid-cols-2 md:col-span-2">
        <div className="space-y-1.5">
          <Label htmlFor={vanId} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <MapPin className="size-3.5" /> {t.from}
          </Label>
          <AddressAutocomplete
            id={vanId}
            value={values.van}
            placeholder={t.placeholder}
            onTextChange={(text) => {
              setValues((v) => ({ ...v, van: text, vanId: "" }));
              setHint(null);
            }}
            onSelect={(place) => {
              setValues((v) => ({ ...v, van: place.label, vanId: place.placeId }));
              setHint(null);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={naarId} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <ArrowRight className="size-3.5" /> {t.to}
          </Label>
          <AddressAutocomplete
            id={naarId}
            value={values.naar}
            placeholder={t.placeholder}
            onTextChange={(text) => {
              setValues((v) => ({ ...v, naar: text, naarId: "" }));
              setHint(null);
            }}
            onSelect={(place) => {
              setValues((v) => ({ ...v, naar: place.label, naarId: place.placeId }));
              setHint(null);
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label
            htmlFor={datumId}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            <CalendarDays className="size-3.5" /> {t.date}
          </Label>
          <input
            id={datumId}
            type="date"
            required
            value={values.datum}
            onChange={(e) => set("datum", e.target.value)}
            className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label
              htmlFor={tijdId}
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Clock className="size-3.5" /> {t.time}
            </Label>
            <TimeSelect
              id={tijdId}
              placeholder={t.timePlaceholder}
              required
              value={values.tijd}
              onChange={(v) => set("tijd", v)}
              className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={personenId} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Users className="size-3.5" /> {t.passengers}
            </Label>
            <Select
              value={String(values.personen)}
              onValueChange={(v) => set("personen", Number(v))}
            >
              <SelectTrigger id={personenId} aria-label={t.passengersAria} className="h-12 w-full text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-end gap-1.5">
        <Button
          type="submit"
          size="lg"
          className="amber-surface h-12 w-full px-8 text-base font-semibold shadow-md md:w-auto"
        >
          {quoting && !quote ? (
            <Loader2 className="mr-1 size-4 animate-spin" />
          ) : null}
          {buttonLabel}
          <ArrowRight className="ml-1 size-4" />
        </Button>
        <div className="min-h-8" aria-live="polite">
          {hint || quoteError ? (
            <p className="text-xs text-destructive">{hint ?? quoteError?.message}</p>
          ) : null}
        </div>
      </div>

      <LeadTimeDialog open={leadDialog} onOpenChange={setLeadDialog} locale={locale} />
    </form>
  );
}

